import {
	COVER_PLACEHOLDER_SRC,
	SHUFFLE_CAROUSEL_ITEM_DURATION_MS,
	SHUFFLE_REVEAL_DELAY_MS,
	SHUFFLE_SAMPLE_LIMIT,
	SHUFFLE_STOP_DECELERATION_MS,
} from '../../shared/config/constants'
import type { ShuffleSampleItem } from '../../shared/types/shuffle'
import { haptic } from '../utils/haptics'
import {
	shuffleAnimationLayerContainerTemplate,
	shuffleFlowFieldTemplate,
} from './shuffleAnimationTemplates'

type ShuffleAnimationOptions = {
	onComplete?: () => void
	preloadedCovers?: string[]
}

type FlowParticleState = {
	element: HTMLElement
	x: number
	width: number
	speed: number
}

type CarouselSlideState = {
	element: HTMLElement
	image: HTMLImageElement
}

const WINNER_INDEX = 0
const ANIMATION_LAYER_SELECTOR = '#shuffle-animation-layer'
const CAROUSEL_TRACK_SELECTOR = '.shuffle-carousel-track'
const RELEASE_COVER_SELECTOR = '#release .cover-wrapper'
const STOP_BUTTON_SELECTOR = '#shuffle-stop-button'
const OVERLAY_SELECTOR = '#overlay-content'
const FLOW_FIELD_SELECTOR = '.shuffle-flow-field'
const FLOW_PARTICLE_SELECTOR = '.shuffle-flow-particle'
const NAV_CLEARANCE_PX = 112
const MIN_LAYER_HEIGHT_PX = 430
const CAROUSEL_SLIDE_COUNT = 4
const CENTER_SLIDE_INDEX = 1
const CAROUSEL_FULL_OPACITY_DISTANCE = 0.4
const CAROUSEL_HIDDEN_DISTANCE = 1.8
const IMAGE_PRELOAD_TIMEOUT_MS = 8000
const MIN_READY_SPIN_COVERS = 8
const SPIN_COVER_START_WAIT_MS = 1500
const FADE_REMOVAL_FALLBACK_MS = 1000
const REVEAL_CLASS_NAMES = [
	'shuffle-cover-only',
	'shuffle-header-only',
	'shuffle-actions-only',
]

class ShuffleAnimationLayerController {
	private readonly hostEl: HTMLElement
	private animationLayerEl: HTMLElement | null = null
	private carouselTrackEl: HTMLElement | null = null
	private releaseCoverEl: HTMLElement | null = null
	private stopButtonEl: HTMLButtonElement | null = null
	private overlayEl: HTMLElement | null = null
	private flowFieldEl: HTMLElement | null = null
	private covers: string[] = []
	private rafId = 0
	private removeLayerTimeoutId = 0
	private lastCarouselTimestamp: number | null = null
	private carouselOffset = 0
	private carouselSlideWidth = 0
	private carouselTravel = 0
	private nextEntryIndex = 0
	private carouselSlides: CarouselSlideState[] = []
	private winnerTargetEntryIndex: number | null = null
	private stopStartedAt: number | null = null
	private stopDurationMs = SHUFFLE_STOP_DECELERATION_MS
	private stopTravelDistance = 0
	private stopTravelledDistance = 0
	private stopStartedFromRest = false
	private hasCarouselMoved = false
	private lastTickTimestamp: number | null = null
	private flowParticles: FlowParticleState[] = []
	private flowFieldWidth = 0
	private hasCompleted = false
	private hasCleanedUp = false
	private readonly prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)',
	).matches
	private readonly sample: ShuffleSampleItem[]
	private readonly preloadedCovers?: string[]
	private readonly onComplete?: () => void
	private eventsController: AbortController | null = null

	constructor(
		hostEl: HTMLElement,
		sample: ShuffleSampleItem[],
		options?: ShuffleAnimationOptions,
	) {
		this.hostEl = hostEl
		this.sample = sample
		this.preloadedCovers = options?.preloadedCovers
		this.onComplete = options?.onComplete

		this.handleStopClick = this.handleStopClick.bind(this)
		this.handleResize = this.handleResize.bind(this)
		this.init()
	}
	private init(): void {
		this.render()
		this.queryElements()

		if (
			!this.animationLayerEl ||
			!this.carouselTrackEl ||
			!this.stopButtonEl
		) {
			this.removeFlowField()
			this.onComplete?.()
			return
		}

		this.prepareHost()
		this.positionAnimationLayer()
		this.prepareFlowField()
		this.addEventListeners()
		this.runAnimation()
		requestAnimationFrame(() => {
			this.animationLayerEl?.classList.remove('is-entering')
		})
	}
	private render(): void {
		this.overlayEl = this.hostEl.closest<HTMLElement>(OVERLAY_SELECTOR)
		this.overlayEl
			?.querySelector<HTMLElement>(`:scope > ${FLOW_FIELD_SELECTOR}`)
			?.remove()
		this.overlayEl?.classList.add('shuffle-flow-active')
		this.overlayEl?.insertAdjacentHTML(
			'afterbegin',
			shuffleFlowFieldTemplate(),
		)
		this.hostEl.insertAdjacentHTML(
			'beforeend',
			shuffleAnimationLayerContainerTemplate(),
		)
	}

	private positionAnimationLayer(): void {
		if (!this.animationLayerEl) return

		this.releaseCoverEl = this.hostEl.querySelector<HTMLElement>(
			RELEASE_COVER_SELECTOR,
		)
		const hostRect = this.hostEl.getBoundingClientRect()
		const coverRect = this.releaseCoverEl?.getBoundingClientRect()
		if (!coverRect?.height) return
		const coverTop = coverRect.top
		const offset = coverTop - hostRect.top
		const availableHeight = window.innerHeight - coverTop - NAV_CLEARANCE_PX

		this.animationLayerEl.style.top = `${offset}px`
		if (coverRect.width) {
			this.animationLayerEl.style.setProperty(
				'--shuffle-cover-size',
				`${coverRect.width}px`,
			)
		}
		this.animationLayerEl.style.setProperty(
			'--shuffle-layer-height',
			`${Math.max(availableHeight, MIN_LAYER_HEIGHT_PX)}px`,
		)
	}
	private addEventListeners(): void {
		this.eventsController?.abort()
		this.eventsController = new AbortController()
		this.stopButtonEl?.addEventListener('click', this.handleStopClick, {
			signal: this.eventsController.signal,
		})
		// Keep the animation still without changing Safari's root scroll container.
		for (const type of ['touchmove', 'wheel']) {
			this.overlayEl?.addEventListener(
				type,
				(event) => {
					if (
						this.overlayEl?.classList.contains(
							'shuffle-flow-active',
						)
					)
						event.preventDefault()
				},
				{ passive: false, signal: this.eventsController.signal },
			)
		}
		window.addEventListener('resize', this.handleResize, {
			signal: this.eventsController.signal,
		})
	}

	private handleStopClick(): void {
		this.beginStop()
	}

	private handleResize(): void {
		if (this.hostEl.classList.contains('shuffle-hidden'))
			this.lockHostHeight()
		this.positionAnimationLayer()
		this.measureCarousel()
		this.prepareFlowField()
	}
	private prepareHost(): void {
		clearReleaseRevealClasses(this.hostEl)
		this.hostEl.classList.add('shuffle-host', 'shuffle-hidden')
		this.lockHostHeight()
	}

	private lockHostHeight(): void {
		// Hydration replaces the hidden release while spinning. Keep that change
		// from moving the overlay footer until the result is revealed.
		this.hostEl.classList.remove('shuffle-size-locked')
		this.hostEl.style.setProperty(
			'--shuffle-host-height',
			`${this.hostEl.getBoundingClientRect().height}px`,
		)
		this.hostEl.classList.add('shuffle-size-locked')
	}

	private runAnimation(): void {
		if (!this.animationLayerEl || !this.carouselTrackEl) {
			this.finish()
			return
		}

		// Keep the preloaded list by reference: covers still loading in the
		// background are appended to it and enter the rotation as slides recycle.
		this.covers = this.preloadedCovers?.length
			? this.preloadedCovers
			: prepareCovers(this.sample)
		if (!this.covers.length) {
			this.finish()
			return
		}

		this.prepareCarousel()
		if (this.prefersReducedMotion) {
			this.finish()
			return
		}
		this.rafId = requestAnimationFrame((timestamp) => this.tick(timestamp))
	}

	private tick(timestamp: number): void {
		if (this.hasCompleted) return
		if (!this.hostEl.isConnected || !this.animationLayerEl?.isConnected) {
			this.cleanup()
			return
		}

		if (!this.releaseCoverEl?.isConnected) {
			this.positionAnimationLayer()
			this.measureCarousel()
		}

		// Spin from the first frame, so it already plays while the overlay
		// slides in.
		this.advanceFlowField(timestamp)
		const isStopping = this.stopStartedAt !== null

		if (isStopping) {
			const progress = this.getStopProgress(timestamp)
			const easedProgress = this.stopStartedFromRest
				? smoothstep(progress)
				: 1 - (1 - progress) ** 3
			const travelledDistance = easedProgress * this.stopTravelDistance
			this.advanceCarousel(travelledDistance - this.stopTravelledDistance)
			this.stopTravelledDistance = travelledDistance

			if (progress >= 1) {
				this.finish()
				return
			}
		} else {
			if (this.lastCarouselTimestamp !== null) {
				const elapsedMs = Math.min(
					timestamp - this.lastCarouselTimestamp,
					50,
				)
				this.advanceCarousel(
					elapsedMs / SHUFFLE_CAROUSEL_ITEM_DURATION_MS,
				)
			}
			this.lastCarouselTimestamp = timestamp
		}

		this.queueNextFrame()
	}

	private beginStop(): void {
		if (this.hasCompleted || this.stopStartedAt !== null) return

		this.stopStartedAt = performance.now()
		const naturalStopDistance =
			SHUFFLE_STOP_DECELERATION_MS /
			(3 * SHUFFLE_CAROUSEL_ITEM_DURATION_MS)
		const coversToWinner = Math.min(
			Math.max(Math.round(naturalStopDistance + this.carouselOffset), 3),
			this.covers.length + 2,
		)
		this.stopTravelDistance = coversToWinner - this.carouselOffset
		this.stopTravelledDistance = 0
		this.stopStartedFromRest = !this.hasCarouselMoved
		this.stopDurationMs = this.stopStartedFromRest
			? SHUFFLE_STOP_DECELERATION_MS
			: 3 * this.stopTravelDistance * SHUFFLE_CAROUSEL_ITEM_DURATION_MS
		this.winnerTargetEntryIndex =
			this.nextEntryIndex +
			coversToWinner -
			(CAROUSEL_SLIDE_COUNT - CENTER_SLIDE_INDEX)

		if (this.stopButtonEl) this.stopButtonEl.disabled = true
		this.animationLayerEl?.classList.add('is-stopping')
	}

	private finish(): void {
		if (this.hasCompleted) return

		this.hasCompleted = true
		if (this.rafId) cancelAnimationFrame(this.rafId)
		this.carouselOffset = 0
		const centeredSlide = this.carouselSlides[CENTER_SLIDE_INDEX]
		if (centeredSlide) this.setSlideEntry(centeredSlide, WINNER_INDEX)
		this.positionCarousel()
		this.revealReleaseCover()
		void runReleaseStaggerReveal(this.hostEl).finally(() => {
			this.cleanup()
		})
	}

	private revealReleaseCover(): void {
		this.hostEl.classList.remove('shuffle-hidden', 'shuffle-size-locked')
		this.hostEl.style.removeProperty('--shuffle-host-height')
		this.hostEl.classList.add('shuffle-cover-only')

		if (!this.animationLayerEl) return
		this.animationLayerEl.style.opacity = '0'
		if (this.flowFieldEl) this.flowFieldEl.style.opacity = '0'
		this.removeLayerAfterFade()
	}

	private cleanup(): void {
		if (this.hasCleanedUp) return
		this.hasCleanedUp = true

		this.eventsController?.abort()
		this.eventsController = null
		if (this.rafId) cancelAnimationFrame(this.rafId)
		this.clearRemoveLayerTimeout()

		this.hostEl.classList.remove(
			'shuffle-host',
			'shuffle-hidden',
			'shuffle-size-locked',
		)
		this.hostEl.style.removeProperty('--shuffle-host-height')
		if (!this.hasCompleted) clearReleaseRevealClasses(this.hostEl)
		this.animationLayerEl?.remove()
		this.removeFlowField()

		this.onComplete?.()
	}
	private queryElements(): void {
		this.overlayEl ??= this.hostEl.closest<HTMLElement>(OVERLAY_SELECTOR)
		this.animationLayerEl = this.hostEl.querySelector<HTMLElement>(
			ANIMATION_LAYER_SELECTOR,
		)
		this.carouselTrackEl =
			this.animationLayerEl?.querySelector<HTMLElement>(
				CAROUSEL_TRACK_SELECTOR,
			) ?? null
		this.releaseCoverEl = this.hostEl.querySelector<HTMLElement>(
			RELEASE_COVER_SELECTOR,
		)
		this.stopButtonEl =
			this.animationLayerEl?.querySelector<HTMLButtonElement>(
				STOP_BUTTON_SELECTOR,
			) ?? null
		this.flowFieldEl =
			this.overlayEl?.querySelector<HTMLElement>(
				`:scope > ${FLOW_FIELD_SELECTOR}`,
			) ?? null
	}

	private prepareFlowField(): void {
		if (!this.flowFieldEl || this.prefersReducedMotion) return

		this.flowFieldWidth = this.flowFieldEl.getBoundingClientRect().width
		if (!this.flowFieldWidth) return

		this.flowParticles = Array.from(
			this.flowFieldEl.querySelectorAll<HTMLElement>(
				FLOW_PARTICLE_SELECTOR,
			),
		).map((element) => {
			const xPercent = Number(element.dataset.flowX) || 0
			const speed = Number(element.dataset.flowSpeed) || 60
			const width = element.getBoundingClientRect().width
			const x = (xPercent / 100) * this.flowFieldWidth
			element.style.left = '0'
			element.style.transform = `translate3d(${x}px, 0, 0)`
			return { element, x, width, speed }
		})
		this.lastTickTimestamp = null
	}

	private advanceFlowField(timestamp: number): void {
		if (!this.flowFieldEl || !this.flowParticles.length) return

		if (this.lastTickTimestamp === null) {
			this.lastTickTimestamp = timestamp
			return
		}

		const elapsedSeconds =
			Math.min(timestamp - this.lastTickTimestamp, 50) / 1000
		this.lastTickTimestamp = timestamp
		const progress = this.getStopProgress(timestamp)
		const speedFactor =
			this.stopStartedAt === null ? 1 : (1 - progress) ** 2

		for (const particle of this.flowParticles) {
			particle.x -= particle.speed * speedFactor * elapsedSeconds
			if (particle.x < -particle.width - 24) {
				particle.x = this.flowFieldWidth + 24
			}
			particle.element.style.transform = `translate3d(${particle.x}px, 0, 0)`
		}
	}

	private getStopProgress(timestamp: number): number {
		if (this.stopStartedAt === null) return 0
		return clamp01((timestamp - this.stopStartedAt) / this.stopDurationMs)
	}

	private queueNextFrame(): void {
		this.rafId = requestAnimationFrame((timestamp) => this.tick(timestamp))
	}

	private prepareCarousel(): void {
		if (!this.carouselTrackEl || !this.covers.length) return

		this.carouselTrackEl.replaceChildren()
		this.carouselSlides = Array.from(
			{ length: CAROUSEL_SLIDE_COUNT },
			(_, index) => {
				const element = document.createElement('div')
				const image = document.createElement('img')
				element.className = 'shuffle-carousel-slide'
				image.alt = ''
				element.append(image)
				this.carouselTrackEl?.append(element)

				const slide = { element, image }
				this.setSlideEntry(slide, index - CENTER_SLIDE_INDEX)
				return slide
			},
		)
		this.nextEntryIndex = CAROUSEL_SLIDE_COUNT - CENTER_SLIDE_INDEX
		this.carouselOffset = 0
		this.hasCarouselMoved = false
		this.measureCarousel()
	}

	private measureCarousel(): void {
		const firstSlide = this.carouselSlides[0]
		if (!firstSlide) return

		const slideWidth = firstSlide.element.getBoundingClientRect().width
		this.carouselSlideWidth = slideWidth
		this.carouselTravel = slideWidth * 1.18
		this.positionCarousel()
	}

	private positionCarousel(): void {
		if (!this.carouselSlideWidth) return

		this.carouselSlides.forEach((slide, index) => {
			const position = index - CENTER_SLIDE_INDEX - this.carouselOffset
			const distance = Math.abs(position)
			const fadeProgress = clamp01(
				(CAROUSEL_HIDDEN_DISTANCE - distance) /
					(CAROUSEL_HIDDEN_DISTANCE - CAROUSEL_FULL_OPACITY_DISTANCE),
			)
			const visibility = smoothstep(fadeProgress)
			const saturation = Math.max(0.55, 1 - distance * 0.25)
			const x =
				position * this.carouselTravel - this.carouselSlideWidth / 2

			slide.element.style.transform = `translate3d(${x}px, 0, 0)`
			slide.element.style.opacity = String(visibility)
			slide.element.style.filter = `saturate(${saturation})`
			slide.element.style.zIndex = String(
				Math.max(0, 10 - Math.round(distance * 5)),
			)
		})
	}

	private advanceCarousel(distance: number): void {
		if (!this.carouselTrackEl || !this.carouselSlides.length) return
		if (distance > 0) this.hasCarouselMoved = true

		this.carouselOffset += distance
		while (this.carouselOffset + 0.0001 >= 1) {
			this.carouselOffset -= 1
			if (this.carouselOffset < 0) this.carouselOffset = 0
			this.recycleCarouselSlide()
		}
		this.positionCarousel()
	}

	private recycleCarouselSlide(): void {
		const firstSlide = this.carouselSlides.shift()
		if (!firstSlide || !this.carouselTrackEl) return

		if (this.nextEntryIndex === this.winnerTargetEntryIndex) {
			this.setSlideEntry(firstSlide, WINNER_INDEX)
		} else {
			this.setSlideEntry(firstSlide, this.nextEntryIndex)
		}
		this.nextEntryIndex += 1
		this.carouselSlides.push(firstSlide)
		this.carouselTrackEl.append(firstSlide.element)
	}

	private setSlideEntry(slide: CarouselSlideState, entryIndex: number): void {
		if (!this.covers.length) return
		const normalizedIndex =
			((entryIndex % this.covers.length) + this.covers.length) %
			this.covers.length
		slide.image.src = this.covers[normalizedIndex]
	}

	private removeLayerAfterFade(): void {
		const layer = this.animationLayerEl
		if (!layer) return

		const remove = () => {
			this.clearRemoveLayerTimeout()
			if (layer.isConnected) layer.remove()
			this.removeFlowField()
		}
		layer.addEventListener(
			'transitionend',
			(event) => {
				if (event.propertyName === 'opacity') remove()
			},
			{ once: true },
		)
		this.removeLayerTimeoutId = window.setTimeout(
			remove,
			FADE_REMOVAL_FALLBACK_MS,
		)
	}

	private clearRemoveLayerTimeout(): void {
		if (!this.removeLayerTimeoutId) return
		window.clearTimeout(this.removeLayerTimeoutId)
		this.removeLayerTimeoutId = 0
	}

	private removeFlowField(): void {
		this.flowFieldEl?.remove()
		this.flowFieldEl = null
		this.overlayEl?.classList.remove('shuffle-flow-active')
	}
}

/**
 * Resolves once the winner and a few spin covers are ready. The returned list
 * keeps growing as the remaining covers load, so the carousel picks them up
 * while it spins. Covers that fail to load are left out of the rotation.
 */
export async function preloadShuffleSample(
	sample: ShuffleSampleItem[],
	signal?: AbortSignal,
): Promise<string[]> {
	const [winner, ...others] = sample.slice(0, SHUFFLE_SAMPLE_LIMIT)
	if (!winner) return []

	const spinCovers = others.flatMap((item) =>
		item.cover_image ? [item.cover_image] : [],
	)
	let readyCovers: string[] = []
	let settledCount = 0
	let checkSpinCovers = () => {}
	const enoughSpinCovers = new Promise<void>((resolve) => {
		checkSpinCovers = () => {
			if (
				readyCovers.length >= MIN_READY_SPIN_COVERS ||
				settledCount === spinCovers.length
			) {
				resolve()
			}
		}
		window.setTimeout(resolve, SPIN_COVER_START_WAIT_MS)
	})
	for (const cover of spinCovers) {
		preloadCover(cover, signal)
			.then(
				(loaded) => {
					if (loaded !== COVER_PLACEHOLDER_SRC)
						readyCovers.push(loaded)
				},
				() => {},
			)
			.finally(() => {
				settledCount += 1
				checkSpinCovers()
			})
	}
	checkSpinCovers()

	const winnerCover = await preloadCover(
		winner.cover_image || COVER_PLACEHOLDER_SRC,
		signal,
	)
	await enoughSpinCovers
	signal?.throwIfAborted()

	const covers = [winnerCover, ...readyCovers]
	// Keep the winner from repeating back to back when nothing else loaded yet.
	if (covers.length === 1) covers.push(COVER_PLACEHOLDER_SRC)
	readyCovers = covers
	return covers
}

export function runReleaseStaggerReveal(host: HTMLElement): Promise<void> {
	if (!host.isConnected) return Promise.resolve()
	void haptic('reveal')
	clearReleaseRevealClasses(host)
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return Promise.resolve()
	}
	host.classList.add('shuffle-cover-only')

	return new Promise((resolve) => {
		const schedule = (callback: () => void) => {
			window.setTimeout(() => {
				if (!host.isConnected) {
					resolve()
					return
				}
				callback()
			}, SHUFFLE_REVEAL_DELAY_MS)
		}

		schedule(() => {
			host.classList.remove('shuffle-cover-only')
			host.classList.add('shuffle-header-only')
			schedule(() => {
				host.classList.remove('shuffle-header-only')
				host.classList.add('shuffle-actions-only')
				schedule(() => {
					host.classList.remove('shuffle-actions-only')
					resolve()
				})
			})
		})
	})
}

export function attachShuffleAnimation(
	sample: ShuffleSampleItem[],
	options?: ShuffleAnimationOptions,
): boolean {
	const host = document.querySelector<HTMLElement>('.overlay-inner-content')
	if (!host) return false
	new ShuffleAnimationLayerController(host, sample, options)
	return true
}

function prepareCovers(sample: ShuffleSampleItem[]): string[] {
	return sample.map((item) => item.cover_image || COVER_PLACEHOLDER_SRC)
}

function clamp01(value: number): number {
	return Math.min(Math.max(value, 0), 1)
}

function smoothstep(value: number): number {
	return value ** 2 * (3 - 2 * value)
}

function preloadCover(cover: string, signal?: AbortSignal): Promise<string> {
	return new Promise((resolve, reject) => {
		const image = new Image()
		let timeoutId = 0
		let settled = false

		const cleanup = () => {
			if (timeoutId) window.clearTimeout(timeoutId)
			image.onload = null
			image.onerror = null
			signal?.removeEventListener('abort', handleAbort)
		}
		const settle = (resolvedCover: string) => {
			if (settled) return
			settled = true
			cleanup()
			resolve(resolvedCover)
		}
		const handleAbort = () => {
			if (settled) return
			settled = true
			cleanup()
			image.src = ''
			reject(
				new DOMException('Shuffle preparation aborted', 'AbortError'),
			)
		}

		if (signal?.aborted) {
			handleAbort()
			return
		}
		signal?.addEventListener('abort', handleAbort, { once: true })
		image.onload = () => settle(cover)
		image.onerror = () => settle(COVER_PLACEHOLDER_SRC)
		timeoutId = window.setTimeout(
			() => settle(COVER_PLACEHOLDER_SRC),
			IMAGE_PRELOAD_TIMEOUT_MS,
		)
		image.src = cover
	})
}

function clearReleaseRevealClasses(host: HTMLElement): void {
	host.classList.remove(...REVEAL_CLASS_NAMES)
}
